import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0504 from "../lesson-04/lesson";
import lesson0518 from "../lesson-18/lesson";
const linkLesson0504 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0504.id);
const linkLesson0518 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0518.id);

const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>When buying or renting a home, an important piece of information might be the presence of <strong><span class=cmbx-10>outdoor</span></strong> noise sources such as traffic, industrial power plants, wind farms, etc., as well as <strong><span class=cmbx-10>indoor</span></strong> noise sources such as music played by neighbours, elevators, washing machines, plumbing, etc. The information that is equally important concerns the ability of the building to provide sufficient protection from unwanted noise by providing adequate sound insulation for the living space in question.<br class=newline>
<p>The usual way of evaluating sound insulation performance is through measurements that yield the values of <strong><span class=cmbx-10>airborne and impact sound insulation</span> <span class=cmbx-10>parameters</span></strong> defined specifically for this purpose.<br class=newline>
<p>The values of <strong><span class=cmbx-10>airborne and impact sound insulation parameters</span></strong> can be <strong><span class=cmbx-10>estimated</span></strong> in the design stage for any building element within a building. The required calculation methods are defined in relevant standards, namely [5] for airborne sound insulation parameters between rooms, [6] for impact sound insulation between rooms, and [1] for airborne sound insulation against outdoor sound.<br class=newline>
<p>The sound insulation performance of building elements is often measured, both in laboratory conditions, when flanking transmissions are reduced as much as possible, and in-situ, when flanking plays a pivotal role in overall sound insulation performance.<br class=newline>
<p>
   This lesson focuses on defining the airborne sound insulation parameters such as the level difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
   </math>
   , standardized level difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>
   , sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   , and apparent sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   , which are ultimately used for classification of different building structures according to sound insulation performance, and on explaining the differences between them.<br class=newline>
<p>
   The lesson also covers the impact sound insulation parameters such as the normalized impact sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   obtained in a laboratory, the normalized impact sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   is obtained in-situ, and standardized impact sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   .<br class=newline>
<p>Building structures that can be evaluated through airborne sound insulation measurements are: walls, the overall facade and facade elements (doors, windows, ventilation elements), floors, and ceilings. The airborne sound sources can be located outside the building (traffic, industry), but also inside it as airborne or structure-borne sound sources (ventilation, elevator and sewer noise, a cafe or a bakery below, an adjacent flat, or neighbours).<br class=newline>The building elements that need to be evaluated regarding the protection from outdoor noise are: outer walls, facades, and individual facade elements such as windows, ventilation ducts or the main door to the building. The building elements that are important for protecting the residents from indoor noise are inner walls, floors, ceilings, and doors.<br class=newline>
<p>
   The block diagram of this lesson is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-1.svg data-img-alt="Figure 1. Block diagram of the lesson.">Figure 1</a>.
    
    <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/22/theory/figure-1.svg alt="Figure 1. Block diagram of the lesson."><figcaption class=caption>Figure 1. Block diagram of the lesson.</figcaption></figure>
<h3 class=sectionHead>Introduction</h3>
<p>The <strong><span class=cmbx-10>laboratory and field measurements</span></strong> of sound insulation parameters are usually done by <strong><span class=cmbx-10>laboratories</span></strong> accredited according to [12]. The measurements are performed according to a family of standards devised for this particular purpose. Naturally, these measurements can be made by anyone who possesses the necessary equipment and is familiarized with measurement procedures. However, only the results obtained from accredited laboratories will have legal relevance.<br class=newline>
<p>The <strong><span class=cmbx-10>field</span></strong> measurements procedures and the equipment necessary for measuring the airborne sound insulation parameters are described in  [8] for inner building elements such as walls, doors, ceilings, and floors. The [9] contains the same information for the outer building elements, namely, for whole facades and individual facade elements (windows). The frequency-dependent airborne sound insulation parameters are measured and obtained in the proposed the frequency range from 50&nbsp;Hz-5000&nbsp;Hz. The corresponding single-number values are evaluated and obtained from frequency-dependent data according to  [10], once the building structure(s) have been installed.<br class=newline>
<p><strong><span class=cmbx-10>Laboratory</span></strong> measurements of airborne sound insulation parameters are described in  [3] and the evaluation procedure for obtaining the single-number values is covered with [10]. The [7] is used to determine measurement uncertainty for both field and laboratory measurements of these parameters.<br class=newline>
<p>The <strong><span class=cmbx-10>basic equipment</span></strong> needed for measuring <strong><span class=cmbx-10>the airborne sound insulation</span> <span class=cmbx-10>parameters</span></strong> consists of:
<ul class=itemize1>
   <li class=itemize>an omnidirectional sound source
   <li class=itemize>a sound level meter for measuring sound pressure levels (spatially- and time-averaged in one-third octave bands) in the source room and the receiving room
   <li class=itemize>the equipment for measuring the reverberation time (usually a sound level meter and an omnidirectional sound source)
   <li class=itemize>the equipment for measuring the geometrical dimensions of the rooms
   <li class=itemize>commercially available or self-developed software support for the calculation of parameters in one-third octave bands or octave bands, and for evaluation of single-number quantities
</ul>
<p>
   The measurement setup for in-situ airborne sound insulation measurements is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-2.svg data-img-alt="Figure 2. Measurement setup for measuring the airborne sound insulation parameters for vertical and horizontal building constructions in in-situ conditions.">Figure 2</a> for a vertical building element such as a wall, and a horizontal building element such as a floor.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/22/theory/figure-2.svg alt="Figure 2. Measurement setup for measuring the airborne sound insulation parameters for vertical and horizontal building constructions in in-situ conditions."><figcaption class=caption>Figure 2. Measurement setup for measuring the airborne sound insulation parameters for vertical and horizontal building constructions in in-situ conditions.</figcaption></figure>
<p>The procedures for field measurements and the necessary equipment for measuring the impact sound insulation parameters are described in  [<b>ISO 16283˙2</b>] for building elements, namely ceilings/floors. The frequency-dependent data is obtained in the proposed frequency range from 50 - 5000&nbsp;Hz, and single-number values are evaluated according to ISO  [10], after the installation of building structure(s). Laboratory measurements of impact sound insulation parameters are described in  [4] in combination with the standard ISO 717-2:2020. In addition, the standard  [11] is used to find the measurement uncertainty for both field and laboratory measurements.<br class=newline>
<p>The <strong><span class=cmbx-10>basic equipment</span></strong> needed for measuring <strong><span class=cmbx-10>the impact sound insulation</span> <span class=cmbx-10>parameters</span></strong> consists of:
<ul class=itemize1>
   <li class=itemize>a source of impact sound (tapping machine, rubber ball or foot steps)
   <li class=itemize>a sound level meter for measuring sound pressure levels (spatially and time averaged in one-third octave bands) in the receiving and the source room
   <li class=itemize>the equipment for measuring the reverberation time (usually a sound level meter and an omnidirectional sound source or a source of impulse noise)
   <li class=itemize>the equipment for measuring the geometrical dimensions of the rooms
   <li class=itemize>commercially available or self-developed software support for the calculation of parameters in one-third octave bands or octave bands, and for evaluation of single-number quantities
</ul>
<p>
   The measurement setup for impact sound insulation measurements is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-3.svg data-img-alt="Figure 3. Measurement setup for impact sound insulation measurements for horizontal and vertical transmission.">Figure 3</a> for horizontal and vertical transmission of impact sound.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/22/theory/figure-3.svg alt="Figure 3. Measurement setup for impact sound insulation measurements for horizontal and vertical transmission."><br class=newline><figcaption class=caption>Figure 3. Measurement setup for impact sound insulation measurements for horizontal and vertical transmission.</figcaption></figure>
<p><strong><span class=cmbx-10>Field (in-situ)</span></strong> measurements of airborne and impact sound insulation parameters are performed <strong><span class=cmbx-10>to check</span></strong> if the sound insulation performance of building element(s) within a building <strong><span class=cmbx-10>meets the requirements stipulated by national</span> <span class=cmbx-10>legislation (bylaws)</span></strong>. The fulfillment of these requirements implies that the necessary protection from harmful noise sources in the environment has been achieved. The source side and the receiving side are separated with the investigated building element(s). Field measurements are done only after the building element has been installed in its permanent place in the building. Flanking transmission is taken into account during field measurements as a fact of life and a real-life situation. Therefore, for the purpose of performing the measurements, nothing is done to reduce flanking transmission. Instead, measurements on the investigated building element are performed ”as is”.<br class=newline>
<p><strong><span class=cmbx-10>The results of laboratory measurements</span></strong> can be used <strong><span class=cmbx-10>to compare</span></strong> the airborne and impact sound insulation performance of different building elements and to <strong><span class=cmbx-10>classify</span></strong> the products of the same type according to their sound insulation performance (walls, floors, ceiling, glazing). This data is usually used in the design stage of the building construction.<br class=newline>
<p>
   In laboratory setups, the <strong><span class=cmbx-10>flanking transmission paths</span></strong> are severed in compliance with the instructions given in  [<b>ISO140˙3</b>]. In essence, there needs to be a dilatation between the source and the receiving room, and it needs to be applied all around the perimeter of the measured building element. An illustration is given in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-4.svg data-img-alt="Figure 4. The method of reducing flanking transmission in laboratory setups.">Figure 4</a> for the laboratory measurement of both vertical and horizontal building elements. Please compare with <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-2.svg data-img-alt="Figure 2. Measurement setup for measuring the airborne sound insulation parameters for vertical and horizontal building constructions in in-situ conditions.">Figure 2</a> to see how the applied dilatation prevents flanking transmission.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/22/theory/figure-4.svg alt="Figure 4. The method of reducing flanking transmission in laboratory setups."><figcaption>Figure 4. The method of reducing flanking transmission in laboratory setups.</figcaption></figure>
<p>If the described measure is applied properly, all flanking transmission is removed in the laboratory setup, and only direct transmission through the investigated partition can be measured.
<h3 class=sectionHead>The definition of airborne sound insulation parameters from sound transmission loss / sound reduction index</h3>
<p>
   The <strong><span class=cmbx-10>airborne sound insulation parameters</span></strong> used in practice are: the
   <strong>
      <span class=cmbx-10>apparent sound reduction index</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>R</mi>
      </math>
      <span class=cmbx-10>’</span>
   </strong>
   , the
   <strong>
      <span class=cmbx-10>standardized level</span> <span class=cmbx-10>difference </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>n</mi>
               <mi>T</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   , and the
   <strong>
      <span class=cmbx-10>normalized level difference </span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>n</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   .<br class=newline>
<p>The values of these parameters for different building elements are chosen by knowing the expected equivalent sound pressure levels of noise generated by different types of outdoor and indoor sound sources, and the <strong><span class=cmbx-10>tentative values of indoor</span> <span class=cmbx-10>sound pressure levels</span></strong> proposed by different regulations. The specific constructions that provide the required sound insulation performance can then be chosen, designed and built.<br class=newline>
<p>
   <strong><span class=cmbx-10>The principal parameter</span></strong> for expressing the <strong><span class=cmbx-10>in-situ airborne</span> <span class=cmbx-10>sound insulation</span></strong> performance is the
   <strong>
      <span class=cmbx-10>apparent sound reduction index</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>′</mi>
            </mrow>
         </msup>
      </math>
   </strong>
   . It takes into account all transmission paths (direct and flanking) in the finished state of the building. All other parameters such as the standardized level difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mi>T</mi>
   </math>
   and the normalized level difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   can be derived from this parameter. The apparent sound reduction index is compared with the tentative values for different building structures. In some countries, the standardized level difference is used instead of the apparent sound reduction index, and the tentative values proposed in regulations are given for this parameter.<br class=newline>The <strong><span class=cmbx-10>laboratory measurements</span></strong> yield the
   <strong>
      <span class=cmbx-10>sound reduction index</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>R</mi>
      </math>
   </strong>
   . It quantifies the airborne sound insulation performance of a building element taking into account only direct transmission through the element, while flanking transmission is suppressed as much as technically possible.<br class=newline>The definition of the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   is given in <a href="${linkLesson0518}" target="_blank">Lesson 18</a> and repeated here as well. The sound reduction index is defined as ten times the logarithm of the ratio of the sound power in the source room
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and the sound power in the receiving room
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   , as:
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>τ</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>In the derivation of the sound reduction index, it is assumed that the sound field is diffuse in both the source room and the receiving room. This means that the sound pressure level is (almost) the same in each point in the room. This assumption is essentially valid at higher frequencies, but at low frequencies the sound pressure distribution in the room is strongly influenced by room resonance that manifests itself through the appearance of standing waves (see <a href="${linkLesson0504}" target="_blank">Lesson 4</a>).<br class=newline>
<p>
   If
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the average sound pressure level in the source room, the sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   incident on the partition of interest is given as [15]
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mi>n</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mi>S</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>ρ</mi>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
            <mi>n</mi>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   is the incident sound intensity in the diffuse field, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is the surface area of the partition. The sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   in the receiving room can be found from the sound power radiated by the partition. This sound pressure level also depends on the total absorption in the receiving room (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   ), the pressure level can be calculated as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>ρ</mi>
            <mi>c</mi>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ρ</mi>
   </math>
   is the density of air, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the speed of sound in air.<br class=newline>By knowing the ratio of the incident sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and the transmitted sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   , the sound reduction index is derived as:
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>ρ</mi>
                  <mi>c</mi>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mfrac>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are spatially- and time-averaged sound pressure levels in the source room and in the receiving room, respectively. They are measured using a sound level meter, usually in one-third octave bands from 50&nbsp;Hz up to 5000&nbsp;Hz. The apostrophe character in the apparent sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   indicates that this parameter is obtained from the results of in-situ measurements, and that flanking transmission has an influence on the obtained result.<br class=newline>The equivalent absorption area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   of the receiving room with a volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   is calculated from the Sabine formula for reverberation time as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>A</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>1</mn>
                  <mn>6</mn>
                  <mn>1</mn>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   As a frequency-dependent parameter, it is calculated in one-third octave bands from the measured values of reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   . The volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   is the net volume of the receiving room, expressed in cubic meters. The volume of the objects in the room such as furniture is subtracted from the gross volume obtained from measured room dimensions.<br class=newline>
<p>Ultimately, all frequency-dependent airborne sound insulation parameters can be evaluated to yield single-number values.
<p>
   Since it takes into account the flanking transmission in in-situ conditions, the apparent sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   typically has lower values than the sound reduction index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   obtained in laboratory conditions. The difference is usually a few decibels.<br class=newline>For the calculation of the apparent sound reduction index, the surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   of the common partition between the rooms must be known. In certain cases this is not possible. For example, sound insulation can be measured between rooms that do not share a common partition.<br class=newline>
<p>
   If the total absorption
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   is referred to the reference value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   = 10
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   , the obtained parameter is called the
   <strong>
      <span class=cmbx-10>normalized level difference</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>n</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   . This parameter is used for measuring and evaluating the airborne sound insulation performance of facades and facade elements, in compliance with ISO 16283-3:2016. The normalized level difference is given as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>The normalized level difference is not used for measurement and evaluation of airborne sound insulation of walls, ceilings and floors, according to ISO 16283-2:2014/A1:2018.<br class=newline>
<p>
   A parameter that serves as an alternative for the apparent sound reduction index is the
   <strong>
      <span class=cmbx-10>standardized level difference</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>n</mi>
               <mi>T</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   . To calculate it, the energy-average sound pressure levels
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   must be measured in the source room and the receiving room, respectively. Additionally, the reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   must be measured in the receiving room. The standardized level difference is then given as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mi>T</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the reference reverberation time. The value chosen for typical dwellings is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   = 0.5 s.<br class=newline>
<p>As it is difficult, especially for laymen, to handle and understand the frequency-dependent data for airborne sound insulation parameters, procedures have been developed to evaluate this raw information and yield the single-number values of these parameters. These procedures are covered with  [10] and are described later in this lesson. The procedure for assessing the measurement uncertainty is covered in  [7], and are described below.
<h3 class=sectionHead>Definition of impact sound insulation parameters</h3>
<p>
   The impact sound insulation quality is quantified with two parameters: the <strong><span class=cmbx-10>normalized impact sound pressure level</span></strong> and the <strong><span class=cmbx-10>standardized impact sound</span> <span class=cmbx-10>pressure level</span></strong>. Both of these parameters can be measured in a laboratory and in situ, and they are measured and displayed as a function of frequency, i.e. in one-third or octave frequency bands.<br class=newline>The normalized impact sound pressure level measured in a laboratory is denoted with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   , and the one measured in situ with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   .<br class=newline>Similarly, the standardized impact sound pressure level measured in a laboratory is denoted with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>
   , and the one measured in situ with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   .<br class=newline>
<p>
   The normalized impact sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mi>n</mi>
   </math>
   measured in situ is given as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   are the 1/3-octave or octave sound pressure levels measured in the receiving room, with a standardized impact source as excitation. The measured values are increased by a correction term related to the total absorption
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   in the receiving room. The reference absorption area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is set to 10
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   , as before.<br class=newline>
<p>
   The standardized impact sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   is defined in a similar fashion, as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   The values
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   again represent the 1/3-octave or octave sound pressure levels measured in the receiving room, with a standardized impact source as excitation. These values are reduced by a correction term related to the reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   in the receiving room. The reference reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is set to 0.5 seconds, as before.<br class=newline>
<p>Single-number values can be derived for impact sound insulation parameters, as described in  [11]. The measurement uncertainty is covered with  [7].
<h3 class=sectionHead>Measurement procedure</h3>
<p>As mentioned above, sound insulation parameters are usually measured <strong><span class=cmbx-10>in</span> <span class=cmbx-10>one-third octave bands</span></strong>. <strong><span class=cmbx-10>The normal frequency range</span></strong> required for determination of single-number values is <strong><span class=cmbx-10>from 100-3150&nbsp;Hz</span></strong>. If required or desired, measurements and evaluation can also be made in the extended frequency range from 50-5000&nbsp;Hz. Once the choice of frequency range has been made, all frequency-dependent parameters must be are determined in that frequency range.<br class=newline>
<p>
   The measurement procedure always includes the measurement of sound pressure levels in the receiving room. Specifically,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   is measured for airborne sound insulation, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is measured for impact sound insulation. In airborne sound insulation measurements, sound pressure levels
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   in the source room must be measured as well. Additional measurements are made in the receiving room, namely, the background noise levels
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   , the reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   , and the geometrical parameters, specifically, room volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   and the surface area of the common partition
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   .<br class=newline>
<p>Measurements can be made in various degrees of precision: survey, engineering and precision. In building acoustics, the engineering-grade measurements are the most common.<br class=newline>
<p><strong><span class=cmbx-10>Survey measurements</span></strong> involve quick measurement procedures that allow a simple and fast estimate of an indicator of acoustic quality. <strong><span class=cmbx-10>Precision-grade</span> <span class=cmbx-10>measurement</span></strong> procedures are used in laboratory environment, with the measurement uncertainty reduced to a minimum. However, precision-grade laboratory procedures are often time consuming.<br class=newline>
<p>
   All measurements of sound pressure levels are done using a <strong><span class=cmbx-10>calibrated</span> <span class=cmbx-10>microphone</span></strong> attached to a sound level meter or connected to a sound card of a computer. Such a microphone must have a flat frequency response and often has a high sensitivity (around 50 mV/Pa) in the frequency range of interest. The microphone correction should be set for diffuse sound field conditions (unlike the free-field correction for outdoor measurements). The microphone positions can either be fixed, or the operator can move the microphone along a circular path or a path of some other shape, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-5.svg data-img-alt="Figure 5. Fixed or moving microphone measurement procedure.">Figure 5</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/22/theory/figure-5.svg alt="Figure 5. Fixed or moving microphone measurement procedure."><figcaption class=caption>Figure 5. Fixed or moving microphone measurement procedure.</figcaption></figure>
<p>
   The sound pressure levels
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   are ususally measured in the extended frequency range in one-third octave bands from 50 to 5000&nbsp;Hz. The <strong><span class=cmbx-10>averaging time</span></strong> is set to 15 s for fixed microphone positions, and 30 s for moving microphone positions. If the measurements are performed in the basic frequency range from 100 to 3150&nbsp;Hz, the averaging time can be shortened to 6 s if fixed microphone positions are used.<br class=newline>
<p>
   The
   <strong>
      <span class=cmbx-10>background noise levels (</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      <span class=cmbx-10>)</span>
   </strong>
   are also measured in the receiving room in each one-third octave band within the frequency range of interest. The level of background noise should be at least 6&nbsp;dB lower than the measured level in the receiving room, in both airborne and impact sound insulation measurements. The measured sound pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   (or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   ) must be corrected to take into account the influence of background noise. The correction must be made in each one-third octave band using the correction formula:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mo class=MathClass-punc>,</mo>
                  <mi>c</mi>
                  <mi>o</mi>
                  <mi>r</mi>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mo class=MathClass-open>(</mo>

            <msup>
               <mrow>
                  <mn>1</mn>
                  <mn>0</mn>
               </mrow>
               <mrow>
                <mfrac>
                  <mrow>
                    <msub>
                      <mi>L</mi>
                      <mn>2</mn>
                    </msub>
                  </mrow>
                  <mrow>
                    <mn>1</mn>
                    <mn>0</mn>
                  </mrow>
                </mfrac>
               </mrow>
            </msup>
            <mo>-</mo>
            <msup>
               <mrow>
                  <mn>1</mn>
                  <mn>0</mn>
               </mrow>
               <mrow>
                <mfrac>
                  <mrow>
                    <msub>
                      <mi>L</mi>
                      <mi>b</mi>
                    </msub>
                  </mrow>
                  <mrow>
                    <mn>1</mn>
                    <mn>0</mn>
                  </mrow>
                </mfrac>
               </mrow>
            </msup>
         <mo class=MathClass-close>)</mo>
         </math>
         <td class=eq-no>(10)
         </table>
            <p>If the difference between the measured level and the background noise level is less than 6&nbsp;dB, the measured level is corrected by a fixed value of -1,3&nbsp;dB.<br class=newline>
            <p>
               The <strong><span class=cmbx-10>measurement positions for sound pressure levels</span></strong> in the room should be chosen having in mind the so-called Schroeder frequency of the room. For a typical furnished room in a dwelling, with a volume of 50
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               , the Schroeder frequency is equal to about 200&nbsp;Hz. In one-third octave frequency bands above the Schroeder frequency, the main assumption is that the sound pressure distribution in the room is not affected by room resonances and is, therefore, sufficiently uniform. Therefore, the difference between the measured sound pressure levels from one position to the next is rather small. In this frequency range, the distance between microphone positions should be at least 0.84 m (
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>λ</mi>
               </math>
               /4 at 200&nbsp;Hz). Due to the increase of the level near room boundaries, the distance of the microphone from any room boundary should be at least 0.48 m. The stated limit distances define a space inside the room within which the microphone should be placed, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-6.svg data-img-alt="Figure 6. The rules for choosing the measurement positions when sound pressure level is determined.">Figure 6</a>.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-6.svg alt="Figure 6. The rules for choosing the measurement positions when sound pressure level is determined."><figcaption class=caption>Figure 6. The rules for choosing the measurement positions when sound pressure level is determined.</figcaption></figure>
            <h4 class=subsectionHead>The low-frequency measurement procedure</h4>
            <p>
               Below the Schroeder frequency, the sound field cannot be viewed as stochastic [2]. Instead, it has a deterministic nature, and, as a result, measurement positions in the middle of the room should be avoided. To accommodate this, a special measurement procedure has been developed for the measurements of sound pressure level at low frequencies (one-third octave bands at 50, 63, and 80&nbsp;Hz), with the measurements positions put into the corners of the room.<br class=newline>If low-frequency measurements need to be performed in very small rooms with room volume smaller than 25
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               , additional measurement positions are chosen according to the procedure shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-7.svg data-img-alt="Figure 7. Measurement positions in corners when low-frequency procedure is necessary.">Figure 7</a>.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-7.svg alt="Figure 7. Measurement positions in corners when low-frequency procedure is necessary."><figcaption class=caption>Figure 7. Measurement positions in corners when low-frequency procedure is necessary.</figcaption></figure>
            <p>In this case, fixed measurement positions in the corners of the room are chosen (two opposite corners per loudspeaker position).<br class=newline>The sound pressure levels in both the source room and the receiving room are measured in additional corner positions and averaged only for 50&nbsp;Hz, 63&nbsp;Hz and 80&nbsp;Hz one-third octave bands. The averaged results obtained in these corner positions are combined with the results obtained according to standard procedures (in the middle of the rooms) to obtain the levels, background noise and reverberation time in the low-frequency range.
            <h4 class=subsectionHead>Measurement of reverberation time</h4>
            <p>
               The sound pressure level depends on the total absorption area in the room, which can be calculated from the net volume of the room and the measured reverberation time.<br class=newline>The reverberation time is frequency-dependent, i.e. it differs from one one-third octave band to the next, and so is the total absorption area.<br class=newline>The measurement of reverberation time in the receiving room is described in  [<b>ISO 3382˙2</b>]. Two measurement methods can be used: the interrupted noise method and the integrated impulse response method. The purpose of the procedure is to determine the decay curve in each one-third octave band, and to calculate the reverberation time
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               based on the 20-dB decay of sound, i.e. from -5&nbsp;dB to -25&nbsp;dB referenced to the maximum value.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-8.svg alt="Figure 8. Reverberation time measurement methods."><figcaption class=caption>Figure 8. Reverberation time measurement methods.</figcaption></figure>
            <p>The number of measurement positions depends on the chosen precision grade. For the engineering precision grade, six measurement positions are commonly used, by combining two source positions with three microphone positions. A rotating microphone can be used as well. In this case, the radius of rotation must be least 0.7 m.<br class=newline>
            <p>The interrupted noise method uses white or pink noise emitted by a loudspeaker. Once the steady state in the room has been achieved, the signal is interrupted and the emission of the loudspeaker stops abruptly. From this time instant, the sound in the room decays and this decay is evaluated. To provide sufficient dynamic range for the measurement, the sound pressure level of sound emitted by the loudspeaker in the steady state should be at least 35&nbsp;dB higher than the level of background noise. This condition must be met in each octave or 1/3-octave band within the frequency range of interest.<br class=newline>
            <p>The integrated impulse response method uses either frequency sweeping signals which have high energy at each frequency, or a device capable of emitting impulse sound that can sufficiently excite the room at each frequency of interest. Once the impulse response has been obtained, it is filtered in each one-third octave band. The decay curves are obtained by backward integration, and the reverberation time can be determined from them.
            <h5 class=subsubsectionHead>Averaging the measurement results</h5>
            <p>
               The results for airborne sound insulation are usually averaged over two positions of the sound source combined with five fixed microphone positions per source position. If a moving microphone is used, one measurement with a moving microphone per source position is sufficient. Detailed instructions about the number of source and microphone positions are given in  [<b>ISO 16283˙1</b>]. The number of positions generally depends on the floor area of the source room and the receiving room (
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mo class=MathClass-rel><</mo>
               </math>
               50
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </math>
               and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mo class=MathClass-rel>></mo>
               </math>
               50
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </math>
               ).<br class=newline>Impact sound insulation parameters are usually measured at four source positions, with either two fixed microphone positions or one rotating microphone position per source position.<br class=newline>The sound insulation parameters are found for each source position, and then averaged with different weighting. In airborne sound insulation measurements, greater weight is given to lower values in the averaging procedure. In impact sound insulation measurements, higher values have greater weighting. This is in compliance with the equations for sound reduction index and normalized impact sound pressure levels.<br class=newline>
            <p>
               The equations used for averaging the measured values per source position (total number of microphone positions is
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>m</mi>
               </math>
               ) are given as:
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msup>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-rel>=</mo>
                        <mo class=MathClass-bin>−</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </mfrac>
                        <munderover accentunder=false accent=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </munderover>
                        <mn>1</mn>
                        <msup>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mfrac>
                                 <mrow>
                                    <mo class=MathClass-bin>−</mo>
                                    <msubsup>
                                       <mrow>
                                          <mi>R</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>i</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>′</mi>
                                       </mrow>
                                    </msubsup>
                                 </mrow>
                                 <mrow>
                                    <mn>1</mn>
                                    <mn>0</mn>
                                 </mrow>
                              </mfrac>
                           </mrow>
                        </msup>
                        <mo class=MathClass-close>)</mo>
                     </math>
                  <td class=eq-no>(11)
            </table>
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msubsup>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-rel>=</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </mfrac>
                        <munderover accentunder=false accent=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </munderover>
                        <mn>1</mn>
                        <msup>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mfrac>
                                 <mrow>
                                    <msubsup>
                                       <mrow>
                                          <mi>L</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>n</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>′</mi>
                                       </mrow>
                                    </msubsup>
                                    <mo class=MathClass-punc>,</mo>
                                    <mi>i</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>1</mn>
                                    <mn>0</mn>
                                 </mrow>
                              </mfrac>
                           </mrow>
                        </msup>
                        <mo class=MathClass-close>)</mo>
                     </math>
                  <td class=eq-no>(12)
            </table>
            <h3 class=sectionHead>Measuring the airborne sound insulation of windows and facades</h3>
            <p>
               Airborne sound insulation parameters of windows and facades can be measured as well using the already described procedure. In this case, the sound source is located outside the building, and the receiving room has an outer wall equipped with a facade and different facade elements. A loudspeaker can be used as a sound source, or traffic noise can be utilized if there is a traffic source nearby. The measurement setup is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-9.svg data-img-alt="Figure 9. Measurement setup for the measurements of airborne sound insulation of facades and facade elements.">Figure 9</a>.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-9.svg alt="Figure 9. Measurement setup for the measurements of airborne sound insulation of facades and facade elements."><figcaption class=caption>Figure 9. Measurement setup for the measurements of airborne sound insulation of facades and facade elements.</figcaption></figure>
            <p>Two measurement procedures exist: the global method and the element method.
            <h4 class=subsectionHead>Global method</h4>
            <p>
               In this measurement setup, the outdoor sound pressure level is measured 2 meters in front of the facade. The indoor sound pressure level in the receiving room is measured as the time- and space-averaged value. Five fixed microphone positions or a moving microphone can be used. The calculated level difference can be normalized or standardized (adjusted to reference reverberation time
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               = 0.5 seconds).<br class=newline>
            <p>
               The standardized level difference with the outdoor sound pressure level
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               measured at a distance of 2 m is given as:
            <table class=equation>
               <tr>
                  <td style="font-size: 12px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>D</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                              <mi>m</mi>
                              <mo class=MathClass-punc>,</mo>
                              <mi>n</mi>
                              <mi>T</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                              <mo class=MathClass-punc>,</mo>
                              <mn>2</mn>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mi>T</mi>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>T</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-close>)</mo>
                        <mi>d</mi>
                        <mi>B</mi>
                     </math>
                  <td class=eq-no>(13)
            </table>
            <p>The loudspeaker should be positioned at least 7 meters away from the centre of the facade, or at least 5 meters in front of the facade.
            <h4 class=subsectionHead>Element method</h4>
            <p>
               The element method is used for determining the airborne sound insulation performance of specific facade elements such as windows. In this case, the outdoor sound pressure level is measured directly on the facade, and it is used in the calculation of the apparent sound reduction index of the investigated element. The results of this type of measurement can be compared with laboratory measurements. The source (loudspeaker) is placed to ensure that the angle of incidence is
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mn>45</mn>
                     <mrow>
                        <mo class=MathClass-bin>∘</mo>
                     </mrow>
                  </msup>
               </math>
               .
            <table class=equation>
               <tr>
                  <td style="font-size: 11px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msubsup>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                              <msup>
                                 <mrow>
                                    <mn>5</mn>
                                 </mrow>
                                 <mrow>
                                    <mo class=MathClass-bin>∘</mo>
                                 </mrow>
                              </msup>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-rel>=</mo>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                              <mo class=MathClass-punc>,</mo>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mi>S</mi>
                           </mrow>
                           <mrow>
                              <mi>A</mi>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-bin>−</mo>
                        <mn>1</mn>
                        <mo class=MathClass-punc>.</mo>
                        <mn>5</mn>
                        <mi>d</mi>
                        <mi>B</mi>
                     </math>
                  <td class=eq-no>(14)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>S</mi>
               </math>
               is area of the test specimen, and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>A</mi>
               </math>
               is the equivalent absorption area of the room. The 1.5&nbsp;dB correction is used to reduce the measured outdoor level
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               . This is done to take into account and to compensate for the reflected sound from the facade as a component in the total sound as the one that is actually measured in front of the facade. When traffic noise is used as the source sound, more angles of incidence are possible. In this case, the correction to be used is -3&nbsp;dB.
            <h3 class=sectionHead>Single-number values</h3>
            <p>The described measurement procedures give results in 21 one-third octave frequency bands that cover the frequency range from 50 to 5000&nbsp;Hz.<br class=newline>Instead of frequency-dependent parameters, it is easier and more practical to use single-number quantities. They facilitate quick comparisons of sound insulation performance between different building structures, and the specifications of the requirements on sound insulation quality can be made much more easily.<br class=newline>The single-number value for the (apparent) sound reduction index is based on a standardized reference curve defined in the basic frequency range from 100 to 3150&nbsp;Hz. The reference curve consists of three different sections with slopes of 9&nbsp;dB, 3&nbsp;dB and 0&nbsp;dB per octave, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-10.svg data-img-alt="Figure 10. An example of the measured apparent sound reduction index, plotted against the original reference curve and the shifted reference curve.">Figure 10</a>. The measured values of the (apparent) sound reduction index are compared with the reference curve.<br class=newline>
            <p>
               <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-10.svg data-img-alt="Figure 10. An example of the measured apparent sound reduction index, plotted against the original reference curve and the shifted reference curve.">Figure 10</a> shows the measured values of the apparent sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </math>
               for a 25 cm thick brick wall with the density
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>ρ</mi>
               </math>
               =1000
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               , covered with a 2.5 cm thick layer of plaster on each side, the density of the plaster being
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>ρ</mi>
               </math>
               =1800
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               . The surface area of the wall surface is equal to
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>S</mi>
               </math>
               =10
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </math>
               . The measured values are plotted against the original reference curve and the shifted reference curve.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-10.svg alt="Figure 10. An example of the measured apparent sound reduction index, plotted against the original reference curve and the shifted reference curve."><figcaption class=caption>Figure 10. An example of the measured apparent sound reduction index, plotted against the original reference curve and the shifted reference curve.</figcaption></figure>
            <p>The procedure of finding the single-number value of an airborne sound insulation parameter relies on finding the so-called unfavourable deviations. They are obtained in each 1/3-octave band by calculating the difference between the shifted reference curve and the measured value of the airborne sound insulation parameter. If the measured value is lower than the value of the shifted reference curve, the difference between them is counted as an unfavourable deviation. If the measured value is higher than the shifted reference curve, the difference between them is counted as a favourable deviation and is not taken into account.<br class=newline>
            <p>The reference curve can be shifted vertically in steps of 0.1&nbsp;dB. The correct position of the reference curve has been found when the sum of all the unfavourable deviations is as close to, but not exceeding 32&nbsp;dB. The limit value of 32&nbsp;dB is chosen to yield the average deviation of 2&nbsp;dB in each of the 16 frequency bands.<br class=newline>The final step is to determine the value of the shifted reference curve in the 1/3-octave band at 500&nbsp;Hz. This value is the single-number value of the appropriate airborne sound insulation parameter.<br class=newline>This procedure is described in detail in [10].<br class=newline>
            <p>The procedure of finding the single-number values for impact sound insulation parameters is essentially the same as the one used for airborne sound insulation parameters, with small differences. The two main differences are the shape of the reference curve, as illustrated in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-11.svg data-img-alt="Figure 11. An example of the measured normalized impact sound pressure level, plotted against the original reference curve and shifted reference curve.">Figure 11</a>, and the way of determining the unfavourable deviations. In particular, if the measured value is higher than the reference curve, the deviation is unfavourable and is taken into account. The rest of the procedure is the same as described above, and a detailed description can be found in ISO 717-2:2020.<br class=newline>
            <p>
               <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-11.svg data-img-alt="Figure 11. An example of the measured normalized impact sound pressure level, plotted against the original reference curve and shifted reference curve.">Figure 11</a> shows the measured values of the normalized impact sound pressure level
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mi>n</mi>
                  <mi>T</mi>
               </math>
               obtained for a typical massive floor/ceiling partition. The load-bearing construction is a 20 cm thick reinforced concrete slab with a density of 2300
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               . It is plastered from below with a 2.5 cm thick layer of plaster with a density of 1800
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               . A floating floor is built on top of the slab. The floating floor consists of a 2 cm thick layer of expanded polystyrene with a density of 20
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               , a 4.5 cm thick cement screed with a density of 2000
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               , and a 1.5 cm thick wooden parquet finish with a density of 800
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               . The original reference curve defined for impact sound insulation, as well as the shifted reference curve is shown as well.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-11.svg alt="Figure 11. An example of the measured normalized impact sound pressure level, plotted against the original reference curve and shifted reference curve."><figcaption class=caption>Figure 11. An example of the measured normalized impact sound pressure level, plotted against the original reference curve and shifted reference curve.</figcaption></figure>
            <p>The procedure of finding the single-number value of the impact sound insulation parameter consists of shifting the reference curve, finding the unfavourable deviations, and making sure that their total sum gets as close to, but not exceed 32&nbsp;dB. At this point, the value of the shifted reference curve at 500&nbsp;Hz is taken as the single-number value of the measured impact sound insulation parameter.
            <h3 class=sectionHead>Estimation of single-number parameters</h3>
            <p>In the design stage of building construction, the single-number parameters can be estimated using different calculation procedures described in ISO 12354 family of standards.
            <h4 class=subsectionHead>Estimation single number of sound reduction index</h4>
            <p>
               The weighted sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               as the single-number parameter that represents the sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>R</mi>
               </math>
               (no flanking transmission) can be estimated from the surface mass
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </math>
               of the building structure. To do so, one can use the following simplified equations that are valid for both massive and lightweight structures:
            <table class=equation>
               <tr>
                  <td style="font-size: 10px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>≈</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mn>1</mn>
                        <mn>8</mn>
                        <mo class=MathClass-open>[</mo>
                        <mi>d</mi>
                        <mi>B</mi>
                        <mo class=MathClass-close>]</mo>
                        <mo class=MathClass-punc>,</mo>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>≤</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mo class=MathClass-open>[</mo>
                        <mi>k</mi>
                        <mi>g</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msup>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-close>]</mo>
                     </math>
                  <td class=eq-no>(15)
            </table>
            <table class=equation>
               <tr>
                  <td style="font-size: 10px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>≈</mo>
                        <mn>2</mn>
                        <mn>3</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <mn>7</mn>
                        <mo class=MathClass-open>[</mo>
                        <mi>d</mi>
                        <mi>B</mi>
                        <mo class=MathClass-close>]</mo>
                        <mo class=MathClass-punc>,</mo>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>></mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mo class=MathClass-open>[</mo>
                        <mi>k</mi>
                        <mi>g</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msup>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-close>]</mo>
                     </math>
                  <td class=eq-no>(16)
            </table>
            <p>
               In a multilayered structure, the total surface mass can be found by calculating the surface masses of individual layers, and then the estimated weighted sound reduction index can be calculated. For example, a 25 cm thick brick wall with a density of 1000 kg/m<sup>3</sup>
               is plastered on both sides with 2.5 cm thick layers of plaster with a density of 1800 kg/m<sup>3</sup>
               . The total surface mass of this structure is equal to:
            <table class=equation>
               <tr>
                  <td style="font-size: 6px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mi>k</mi>
                        <mi>g</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msup>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>∗</mo>
                        <mn>0</mn>
                        <mo class=MathClass-punc>.</mo>
                        <mn>2</mn>
                        <mn>5</mn>
                        <mi>m</mi>
                        <mo class=MathClass-bin>+</mo>
                        <mn>2</mn>
                        <mo class=MathClass-bin>∗</mo>
                        <mn>1</mn>
                        <mn>8</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mi>k</mi>
                        <mi>g</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msup>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>∗</mo>
                        <mn>0</mn>
                        <mo class=MathClass-punc>.</mo>
                        <mn>0</mn>
                        <mn>2</mn>
                        <mn>5</mn>
                        <mi>m</mi>
                        <mo class=MathClass-rel>=</mo>
                        <mn>3</mn>
                        <mn>4</mn>
                        <mn>0</mn>
                        <mi>k</mi>
                        <mi>g</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <msup>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                        </msup>
                     </math>
                  <td class=eq-no>(17)
            </table>
            <p>
               The estimated single-number sound reduction index is equal to
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               = 51&nbsp;dB, and this estimate is, in fact, 6&nbsp;dB higher than the value measured in situ due to the influence of flanking transmission in in-situ conditions. The procedure for estimating the influence of flanking transmission is described in detail in EN ISO 12354-1:2000.
            <h4 class=subsectionHead>Estimation of single-number normalized impact sound pressure level</h4>
            <p>An estimation procedure similar to the one for airborne sound insulation parameters is applied for impact sound insulation parameters.<br class=newline>
            <p>
               The weighted normalized impact sound pressure level
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               can be estimated for homogeneous structures such as reinforced concrete slabs using the following equation:
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                              <mo class=MathClass-punc>,</mo>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <mn>1</mn>
                        <mn>6</mn>
                        <mn>4</mn>
                        <mo class=MathClass-bin>−</mo>
                        <mn>3</mn>
                        <mn>5</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>m</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-close>)</mo>
                     </math>
                  <td class=eq-no>(18)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>m</mi>
               </math>
               is the surface mass of the structure, and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </math>
               is the reference surface mass.<br class=newline>
            <p>
               For example, a typical 20 cm thick reinforced concrete slab with a density of
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>ρ</mi>
               </math>
               =2300
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               will have the estimated value of the weighted normalized impact sound pressure level
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mi>n</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>w</mi>
               </math>
               = 70.8&nbsp;dB. As this is too high a value to meet any reasonable requirements, additional layers need to be added to the floor construction to reduce impact (structure-borne) sound. These layers are added on top of the concrete slab, i.e. on the source side. The idea is to have an additional vibrating mass sitting on an elastic layer (spring), as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-12.svg data-img-alt="Figure 12. The principle diagram of a floating floor construction used for impact noise reduction.">Figure 12</a>.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-12.svg alt="Figure 12. The principle diagram of a floating floor construction used for impact noise reduction."><figcaption class=caption>Figure 12. The principle diagram of a floating floor construction used for impact noise reduction.</figcaption></figure>
            <p>The floating floor is essentially a mass-spring system with the resonance frequency given as:
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>f</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                              <mi>π</mi>
                           </mrow>
                        </mfrac>
                        <msqrt>
                           <mrow>
                              <mfrac>
                                 <mrow>
                                    <msub>
                                       <mrow>
                                          <mi>k</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>d</mi>
                                       </mrow>
                                    </msub>
                                 </mrow>
                                 <mrow>
                                    <msub>
                                       <mrow>
                                          <mi>m</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>e</mi>
                                       </mrow>
                                    </msub>
                                 </mrow>
                              </mfrac>
                           </mrow>
                        </msqrt>
                     </math>
                  <td class=eq-no>(19)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </math>
               is the dynamic stiffness of the elastic material used as the spring in the floating floor system (EPS or XPS, mineral wool, etc.) given in
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>M</mi>
                  <mi>N</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               , and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </math>
               is the surface mass of the vibrating mass that sits on the elastic layer.<br class=newline>
            <p>
               In typical situations, 4-5 cm thick concrete screed with a density of 1800
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
                  <mi>g</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               is used as the vibrating mass, so the resonance frequency is usually set below 100&nbsp;Hz.<br class=newline>
            <p>
               If the resonance frequency of the floating floor
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               is known, the additional insertion loss in impact sound pressure level achieved by adding the floating floor is given as:
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mo class=MathClass-bin>△</mo>
                        <mi>L</mi>
                        <mo class=MathClass-rel>=</mo>
                        <mn>4</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mi>f</mi>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>f</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-close>)</mo>
                     </math>
                  <td class=eq-no>(20)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>f</mi>
               </math>
               = 500&nbsp;Hz is the frequency at which single-number values are determined during the evaluation procedure.<br class=newline>
            <p>
               For example, a floating floor with the resonance frequency
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               = 80&nbsp;Hz will improve (reduce) the impact noise in terms of single-number rating by
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mo class=MathClass-bin>△</mo>
                  <mi>L</mi>
               </math>
               = 31.8&nbsp;dB.<br class=newline>Therefore, the final value of the weighted normalized impact sound pressure level is
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               = 70.8&nbsp;dB - 31.8&nbsp;dB = 39&nbsp;dB. This is a very low value that will satisfy any regulations. The maximum permissible values for
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               in the EU differ significantly from one country to the next. For example, Austria has set this value to 48&nbsp;dB, whereas Croatia permits up to 68&nbsp;dB. Bear in mind that stricter demands on impact sound insulation are given with lower values of relevant single-number parameters.
            <h3 class=sectionHead>Spectrum adaptation terms</h3>
            <p>To assess the sound insulation quality of a building structure in relation with specific types of noise, weighting can be applied to the measured values of frequency-dependent parameters. In this manner, the single-number parameters can be complemented by the so-called spectrum adaptation terms.<br class=newline>
            <p>When assessing the airborne sound insulation quality, the standard spectra defined for two different groups of noise sources are used as weighting, and are included in the calculations of the single-number values with spectrum adaptation terms.<br class=newline>
            <p>
               Spectral adaptation can be applied to all single-number airborne sound insulation parameters
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mi>T</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               , and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               that are derived from the respective frequency-dependent parameters
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>R</mi>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mi>T</mi>
                     </mrow>
                  </msub>
               </math>
               , and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </math>
               .<br class=newline>
            <p>The procedure for obtaining the values of the spectrum adaptation terms is described in detail in ISO 717-2:2020. A spectrum adaptation term modifies the single-number value of the parameter it is added to, and the final value is lower in most cases.<br class=newline>
            <p>
               The spectrum adaptation term
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>C</mi>
               </math>
               is used for assessing the airborne sound insulation quality when it comes to protection from sound/noise with dominant middle- and high-frequency content. This can be sound/noise generated by living activities (the TV, radio, music, talking, high-speed road traffic at speeds exceeding 80 km/h), and also sound generated by the industry that contains mostly middle- and high-frequency components.<br class=newline>The spectrum adaptation term
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </math>
               is used for assessing the airborne sound insulation quality when it comes to protection from low-frequency sound/noise such as traffic at low-speeds in cities, and industrial noise with dominant low- and medium-frequency content.<br class=newline>
            <p>
               The spectra (spectrum 1 and spectrum 2) used for calculating the spectrum adaptation terms
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>C</mi>
               </math>
               and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </math>
               , respectively, are defined in [10], as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-13.svg data-img-alt="Figure 13. The spectra used for the calculation of the spectral adaptation terms.">Figure 13</a>.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-13.svg alt="Figure 13. The spectra used for the calculation of the spectral adaptation terms."><figcaption class=caption>Figure 13. The spectra used for the calculation of the spectral adaptation terms.</figcaption></figure>
            <p>Spectrum 1 is given in the frequency range from 50&nbsp;Hz (or 100&nbsp;Hz) up to 5000&nbsp;Hz, or in the frequency range from 50&nbsp;Hz (or 100Hz) up to 3150&nbsp;Hz. In essence, spectrum 1 is the A-weighted spectrum of pink noise.<br class=newline>Spectrum 2 is given in the frequency range from 50&nbsp;Hz to 5000&nbsp;Hz, and represents the A-weighted spectrum of standard traffic noise.<br class=newline>Given that there are two lower limiting frequencies (50&nbsp;Hz and 100&nbsp;Hz), and two upper limiting frequencies (3150&nbsp;Hz and 5000&nbsp;Hz), a total of four frequency ranges can be defined, and the spectrum adaptation terms can be calculated in all of them.<br class=newline>All three spectra are normalized, which means that the energy sum of all the values in individual 1/3-octave bands yields 0&nbsp;dBA. As shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-13.svg data-img-alt="Figure 13. The spectra used for the calculation of the spectral adaptation terms.">Figure 13</a>, the normalized traffic noise spectrum has much more energy at low frequencies compared to the spectrum of pink noise. Therefore, it adds much more weight to low-frequency noise.<br class=newline>
            <p>
               The spectrum adaptation terms
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>C</mi>
               </math>
               or
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </math>
               can be calculated from the known frequency-dependent values of the relevant airborne sound insulation parameters, and from the defined frequency-dependent values of the spectrum of interest, as:
            <table class=equation>
               <tr>
                  <td style="font-size: 13px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>j</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <mo class=MathClass-bin>−</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <munderover accentunder=false accent=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </munderover>
                        <mn>1</mn>
                        <msup>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <msub>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>i</mi>
                                    <mo class=MathClass-punc>,</mo>
                                    <mi>j</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>−</mo>
                              <msub>
                                 <mrow>
                                    <mi>X</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>i</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-bin>∕</mo>
                              <mn>1</mn>
                              <mn>0</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>X</mi>
                           </mrow>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                     </math>
                  <td class=eq-no>(21)
            </table>
            <p>In the equation above,
            <ul class=itemize1>
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi>j</mi>
                        </mrow>
                     </msub>
                  </math>
                  is the spectrum adaptation term of choice (
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>C</mi>
                  </math>
                  or
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi>t</mi>
                           <mi>r</mi>
                        </mrow>
                     </msub>
                  </math>
                  ) calculated in the selected frequency range (100-3150&nbsp;Hz, 100-5000&nbsp;Hz, 50-3150&nbsp;Hz or 50-5000&nbsp;Hz)
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>j</mi>
                  </math>
                  is the index that denotes the chosen sound spectrum (1 or 2) for the calculation of
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>C</mi>
                  </math>
                  or
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi>t</mi>
                           <mi>r</mi>
                        </mrow>
                     </msub>
                  </math>
                  , respectively
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>i</mi>
                  </math>
                  is the index that denotes the one-third octave band in the chosen frequency range (if there are
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>n</mi>
                  </math>
                  one-third octave bands in the chosen frequency range,
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>i</mi>
                  </math>
                  will be in the range from 1 to
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>n</mi>
                  </math>
                  )
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>L</mi>
                        </mrow>
                        <mrow>
                           <mi>i</mi>
                           <mo class=MathClass-punc>,</mo>
                           <mi>j</mi>
                        </mrow>
                     </msub>
                  </math>
                  is the level of the chosen spectrum (j = 1 or 2) in the one-third octave band
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>i</mi>
                  </math>
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>X</mi>
                        </mrow>
                        <mrow>
                           <mi>i</mi>
                        </mrow>
                     </msub>
                  </math>
                  is the measured value of the corresponding frequency-dependent sound insulation parameter in the one-third octave band
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>i</mi>
                  </math>
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>X</mi>
                        </mrow>
                        <mrow>
                           <mi>w</mi>
                        </mrow>
                     </msub>
                  </math>
                  is the original single-number quantity calculated from the measured data using the procedure with the shifted reference curve
            </ul>
            <p>
               The spectrally adapted single-number quantities can be expressed as shown in the example below. In this example, the frequency-dependent sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>R</mi>
               </math>
               was measured in laboratory conditions. From this data, the single-number value of the weighted sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
               </math>
               was determined using the procedure that involves shifting of the reference curve. Using the two defined standard spectra, the spectrum adaptation terms
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>C</mi>
               </math>
               and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </math>
               were found. The adapted single-number quantities were named
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>p</mi>
                        <mi>i</mi>
                        <mi>n</mi>
                        <mi>k</mi>
                     </mrow>
                  </msub>
               </math>
               and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                        <mi>a</mi>
                        <mi>f</mi>
                        <mi>f</mi>
                        <mi>i</mi>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </math>
               , although it is quite common to express them simply as
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>C</mi>
               </math>
               and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </math>
               .
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>p</mi>
                              <mi>i</mi>
                              <mi>n</mi>
                              <mi>k</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi>C</mi>
                     </math>
                  <td class=eq-no>(22)
            </table>
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                              <mi>r</mi>
                              <mi>a</mi>
                              <mi>f</mi>
                              <mi>f</mi>
                              <mi>i</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                              <mi>r</mi>
                           </mrow>
                        </msub>
                     </math>
                  <td class=eq-no>(23)
            </table>
            <p>The purpose of the spectrum adaptation terms being used with single number values is to expose potential weaknesses in airborne sound insulation performance of a building structure, depending on the type of the noise source and the spectral content of noise it generates. In buildings designed and constructed for dwelling, the traffic noise spectrum is more useful to estimate the sound insulation quality of windows and facades, whereas the pink noise spectrum is useful to assess the sound insulation quality of inner structures that should protect an apartment from living noise coming from adjacent flats. Other types of spectra can also be used using the same procedure, although they have not been standardized in any way.<br class=newline>
            <p>
               The single-number values with the spectrum adaptation terms are usually given as
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               (
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>C</mi>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </math>
               ). For example, the weighted apparent sound reduction index of a wall with both spectral adaptation terms can be equal to
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               = 44 (0,-3).<br class=newline>The example above refers to the standard frequency range from 100 to 3150&nbsp;Hz. If the spectrum adaptation terms were calculated in other possible frequency ranges, this will be explicitly expressed and they will be named appropriately:
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mi>–</mi>
                        <mn>3</mn>
                        <mn>1</mn>
                        <mn>5</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               or
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mi>–</mi>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               or
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mi>–</mi>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               , and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mi>–</mi>
                        <mn>3</mn>
                        <mn>1</mn>
                        <mn>5</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               or
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mi>–</mi>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               or
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>r</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mi>–</mi>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               .<br class=newline>
            <p>
               Spectrum adaptation terms are also used with single-number parameters defined for impact sound insulation.<br class=newline>The spectrum adaptation term
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                  </msub>
               </math>
               takes into account the impact spectrum of human walking, and can be calculated as:
            <table class=equation>
               <tr>
                  <td style="font-size: 13px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>I</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <munderover accentunder=false accent=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </munderover>
                        <mn>1</mn>
                        <msup>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <msub>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>i</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-bin>∕</mo>
                              <mn>1</mn>
                              <mn>0</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>−</mo>
                        <mn>1</mn>
                        <mn>5</mn>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>X</mi>
                           </mrow>
                           <mrow>
                              <mi>w</mi>
                           </mrow>
                        </msub>
                     </math>
                  <td class=eq-no>(24)
            </table>
            <p>
               It is calculated in the standard frequency range from 100&nbsp;Hz to 3150&nbsp;Hz. If the measurements are performed in the frequency range extended to low frequencies, the
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>I</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mo class=MathClass-bin>−</mo>
                        <mn>2</mn>
                        <mn>5</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </math>
               spectrum adaptation term is used instead.<br class=newline>
            <p>In the equation above,
            <ul class=itemize1>
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>i</mi>
                  </math>
                  is the index of the one-third octave band within the frequency range of interest
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>L</mi>
                        </mrow>
                        <mrow>
                           <mi>i</mi>
                        </mrow>
                     </msub>
                  </math>
                  is the level of the measured impact sound pressure level in the
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <mi>i</mi>
                  </math>
                  th frequency band, given in 0.1&nbsp;dB precision
               <li class=itemize>
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>X</mi>
                        </mrow>
                        <mrow>
                           <mi>w</mi>
                        </mrow>
                     </msub>
                  </math>
                  is the original single-number quantity calculated using the shifted reference curve procedure
               <li class=itemize>
                  the constant of -15&nbsp;dB ensures that the value of
                  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi>I</mi>
                        </mrow>
                     </msub>
                  </math>
                  is approximately zero for solid floors with effective floor covering.
            </ul>
            <h3 class=sectionHead>Measurement uncertainty</h3>
            <p>All measured input parameters used for calculating single-number quantities exhibit a certain degree of measurement uncertainty due to the influence of different factors, such as the available measurement equipment, the chosen measurement procedure with fixed or moving microphones, the complexity of the sound field in the room, etc.<br class=newline>
            <p>
               <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-14.svg data-img-alt="Figure 14. Sound pressure levels in the source and receiving room with the levels of residual noise in the receiving room.">Figure 14</a> shows the individual sound pressure levels
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               obtained in the source room at 6 fixed microphone positions with 2 source positions, and the individual sound pressure levels
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </math>
               obtained at 6 fixed measurement positions (per source position) in the receiving room.<br class=newline><a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-15.svg data-img-alt="Figure 15. Reverberation time measurement results.">Figure 15</a> shows the results of reverberation time measurements in the receiving room.<br class=newline>The receiving room has a volume
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>V</mi>
               </math>
               =30
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msup>
               </math>
               .
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-14.svg alt="Figure 14. Sound pressure levels in the source and receiving room with the levels of residual noise in the receiving room."><figcaption class=caption>Figure 14. Sound pressure levels in the source and receiving room with the levels of residual noise in the receiving room.</figcaption></figure><figure class=figure><p><img src=${config.ASSETS_URL}/05/22/theory/figure-15.svg alt="Figure 15. Reverberation time measurement results."><figcaption class=caption>Figure 15. Reverberation time measurement results.</figcaption></figure>
            <p>Due to the evident deviations in the measured values, the sound insulation parameter calculated from these values will also exhibit some uncertainty. The displayed charts clearly show that the measurement uncertainty is the highest at low frequencies, mostly due to room resonance. At high frequencies the sound field is almost ideally diffuse, and the measurement uncertainty is quite low.<br class=newline>
            <p>There are two main principles for calculation of measurement uncertainty in building acoustics. The first principle is based on the use of standard deviations in reproducibility conditions from interlaboratory comparisons. The results for standard deviations in reproducibility conditions (averaged from different interlaboratory comparisons) are given in [7] for individual one-third octave bands, and for the single-number parameters.<br class=newline>
            <p>The simplified procedure for the determination of the experimental measurement uncertainty of measured sound insulation parameter is to add these standard deviations in reproducibility conditions to obtain upper and lower curve of the measured sound insulation parameter in one-third octave bands, calculate the upper and lower single-number value of measured parameter, the difference of which is divided by 2, thus yielding the experimental measurement uncertainty of single number value.<br class=newline>
            <p>The second principle is to find the experimental measurement uncertainty of each individual measurement parameter (sound pressure levels in the source and/or receiving room, reverberation time, background noise, geometrical parameters) and to combine them (assuming that there is no correlation between them or find the correlation between some parameters), in order to obtain the overall measurement uncertainty [14], [13].
            <p>
               It s rather complicated to find the measurement uncertainty based on an individual measurement of each involved parameter because an individual measurement includes all parameters (levels, reverberation time, geometry) and their functional dependence. Firstly, the standard deviations of each individual parameter should be found. From the known relationship between the output and the input parameters, and the known sensitivity coefficients and the correlation coefficient, the measurement uncertainty of the output parameter can be found.<br class=newline>Let us assume that a measured quantity
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>Y</mi>
               </math>
               (sound pressure level, reverberation time, surface area, etc.) will be determined from
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>N</mi>
               </math>
               measurements of independent variables
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
               </math>
               ,..,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                  </msub>
               </math>
               . The quantity
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>Y</mi>
               </math>
               will then be a function of those variables, and this can be written as
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>Y</mi>
               </math>
               = f(
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
               </math>
               ,..
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                  </msub>
               </math>
               .…
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                  </msub>
               </math>
               ). The values
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
               </math>
               ,..,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </math>
               are estimates of the input quantities
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </math>
               ,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-punc>,</mo>
               </math>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
               </math>
               ,..,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                  </msub>
               </math>
               . Each estimate
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </math>
               will have an associated uncertainty
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mi>i</mi>
                  <mo class=MathClass-close>)</mo>
               </math>
               which is expressed from the experimental standard deviation as:
            <table class=equation>
               <tr>
                  <td style="font-size: 13px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mi>σ</mi>
                        <mo class=MathClass-rel>=</mo>
                        <mi>s</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-rel>=</mo>
                        <msqrt>
                           <mrow>
                              <mfrac>
                                 <mrow>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>N</mi>
                                 </mrow>
                              </mfrac>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>i</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>n</mi>
                                 </mrow>
                              </munderover>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>(</mo>
                                    <msub>
                                       <mrow>
                                          <mi>x</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>i</mi>
                                       </mrow>
                                    </msub>
                                    <mo class=MathClass-bin>−</mo>
                                    <mover accent=false class=mml-overline>
                                       <mrow>
                                          <mi>X</mi>
                                       </mrow>
                                       <mo accent=true>¯</mo>
                                    </mover>
                                    <mo class=MathClass-close>)</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </msqrt>
                     </math>
                  <td class=eq-no>(25)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mover accent=false class=mml-overline>
                     <mrow>
                        <mi>X</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </math>
               is the averaged value.<br class=newline>
            <p>The standard deviation of the mean value is given by the standard deviation of the observations divided by the square root of the number of observations, as:
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mi>u</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-rel>=</mo>
                        <mfrac>
                           <mrow>
                              <mi>s</mi>
                              <mo class=MathClass-open>(</mo>
                              <msub>
                                 <mrow>
                                    <mi>x</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>i</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <msqrt>
                                 <mrow>
                                    <mi>n</mi>
                                 </mrow>
                              </msqrt>
                           </mrow>
                        </mfrac>
                     </math>
                  <td class=eq-no>(26)
            </table>
            <p>The equation for the experimental standard deviation in repeatability conditions is valid if the difference between the measured values (expressed in&nbsp;dB) are small. In the general case, a more correct form of this equation is given when the measured quantities in&nbsp;dB are converted to relative numbers and vice versa, as:
            <table class=equation>
               <tr>
                  <td style="font-size: 10px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mi>u</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-rel>=</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mn>1</mn>
                        <msup>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mn>0</mn>
                              <mo class=MathClass-punc>.</mo>
                              <mn>1</mn>
                              <msub>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>A</mi>
                                    <mi>V</mi>
                                    <mi>G</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>+</mo>
                        <mi>S</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>A</mi>
                              <mi>V</mi>
                              <mi>G</mi>
                           </mrow>
                        </msub>
                     </math>
                  <td class=eq-no>(27)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>A</mi>
                        <mi>V</mi>
                        <mi>G</mi>
                     </mrow>
                  </msub>
               </math>
               is the energy averaged sound pressure level of
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </math>
               independent measurements, given as:
            <table class=equation>
               <tr>
                  <td style="font-size: 14px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>A</mi>
                              <mi>V</mi>
                              <mi>G</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-rel>=</mo>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mi>l</mi>
                        <mi>o</mi>
                        <mi>g</mi>
                        <mo class=MathClass-open>(</mo>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>N</mi>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-open>(</mo>
                        <munderover accentunder=false accent=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>N</mi>
                           </mrow>
                        </munderover>
                        <mn>1</mn>
                        <msup>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                           <mrow>
                              <mfrac>
                                 <mrow>
                                    <msub>
                                       <mrow>
                                          <mi>L</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>i</mi>
                                       </mrow>
                                    </msub>
                                 </mrow>
                                 <mrow>
                                    <mn>1</mn>
                                    <mn>0</mn>
                                 </mrow>
                              </mfrac>
                           </mrow>
                        </msup>
                        <mo class=MathClass-close>)</mo>
                     </math>
                  <td class=eq-no>(28)
            </table>
            <p>This equation is valid only if each of the performed measurements has the same duration. If not, an additional time weighting should be used when calculating the averaged value. However, in building acoustics, the measurement time is always the same per measurement point, as required by relevant standards.<br class=newline>
            <p>
               The value
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>S</mi>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </math>
               is obtained by converting the measured values (in&nbsp;dB) into relative numbers, as:
            <table class=equation>
               <tr>
                  <td style="font-size: 12px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mi>S</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-rel>=</mo>
                        <msqrt>
                           <mrow>
                              <mfrac>
                                 <mrow>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>N</mi>
                                 </mrow>
                              </mfrac>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>n</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>N</mi>
                                 </mrow>
                              </munderover>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>(</mo>
                                    <mn>1</mn>
                                    <msup>
                                       <mrow>
                                          <mn>0</mn>
                                       </mrow>
                                       <mrow>
                                          <mfrac>
                                             <mrow>
                                                <msub>
                                                   <mrow>
                                                      <mi>L</mi>
                                                   </mrow>
                                                   <mrow>
                                                      <mi>i</mi>
                                                   </mrow>
                                                </msub>
                                             </mrow>
                                             <mrow>
                                                <mn>1</mn>
                                                <mn>0</mn>
                                             </mrow>
                                          </mfrac>
                                       </mrow>
                                    </msup>
                                    <mo class=MathClass-bin>−</mo>
                                    <mn>1</mn>
                                    <msup>
                                       <mrow>
                                          <mn>0</mn>
                                       </mrow>
                                       <mrow>
                                          <mfrac>
                                             <mrow>
                                                <msub>
                                                   <mrow>
                                                      <mi>L</mi>
                                                   </mrow>
                                                   <mrow>
                                                      <mi>A</mi>
                                                      <mi>V</mi>
                                                      <mi>G</mi>
                                                   </mrow>
                                                </msub>
                                             </mrow>
                                             <mrow>
                                                <mn>1</mn>
                                                <mn>0</mn>
                                             </mrow>
                                          </mfrac>
                                       </mrow>
                                    </msup>
                                    <mo class=MathClass-close>)</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </msqrt>
                     </math>
                  <td class=eq-no>(29)
            </table>
            <p>
               If the standard measurement uncertainty (
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </math>
               ) for each measured value is found, the overall measurement uncertainty can be found from:
            <table class=equation>
               <tr>
                  <td>
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mi>u</mi>
                        <mo class=MathClass-rel>=</mo>
                        <msqrt>
                           <mrow>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>i</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>M</mi>
                                 </mrow>
                              </munderover>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>(</mo>
                                    <mfrac>
                                       <mrow>
                                          <mi>∂</mi>
                                          <mi>f</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>∂</mi>
                                          <msub>
                                             <mrow>
                                                <mi>x</mi>
                                             </mrow>
                                             <mrow>
                                                <mi>i</mi>
                                             </mrow>
                                          </msub>
                                       </mrow>
                                    </mfrac>
                                    <mo class=MathClass-close>)</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>(</mo>
                                    <mi>u</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <msub>
                                       <mrow>
                                          <mi>x</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>i</mi>
                                       </mrow>
                                    </msub>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-close>)</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </msqrt>
                     </math>
                  <td class=eq-no>(30)
            </table>
            <p>If the measured variables are correlated, then the equation is a bit more complicated.<br class=newline>
            <p>
               As a building acoustics example, assuming that the input parameters are not correlated, the equation for the experimental measurement uncertainty of the apparent sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </math>
               in each one-third octave band is given as:
            <table class=equation>
               <tr>
                  <td style="font-size: 3px">
                     <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                        <mi>u</mi>
                        <mo class=MathClass-open>(</mo>
                        <msup>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-rel>=</mo>
                        <msqrt>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>[</mo>
                                    <msub>
                                       <mrow>
                                          <mi>c</mi>
                                       </mrow>
                                       <mrow>
                                          <msub>
                                             <mrow>
                                                <mi>L</mi>
                                             </mrow>
                                             <mrow>
                                                <mn>1</mn>
                                             </mrow>
                                          </msub>
                                       </mrow>
                                    </msub>
                                    <mi>u</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <msub>
                                       <mrow>
                                          <mi>L</mi>
                                       </mrow>
                                       <mrow>
                                          <mn>1</mn>
                                       </mrow>
                                    </msub>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-close>]</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-bin>+</mo>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>[</mo>
                                    <msub>
                                       <mrow>
                                          <mi>c</mi>
                                       </mrow>
                                       <mrow>
                                          <msub>
                                             <mrow>
                                                <mi>L</mi>
                                             </mrow>
                                             <mrow>
                                                <mn>2</mn>
                                             </mrow>
                                          </msub>
                                       </mrow>
                                    </msub>
                                    <mi>u</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <msub>
                                       <mrow>
                                          <mi>L</mi>
                                       </mrow>
                                       <mrow>
                                          <mn>2</mn>
                                       </mrow>
                                    </msub>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-close>]</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-bin>+</mo>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>[</mo>
                                    <msub>
                                       <mrow>
                                          <mi>c</mi>
                                       </mrow>
                                       <mrow>
                                          <msub>
                                             <mrow>
                                                <mi>L</mi>
                                             </mrow>
                                             <mrow>
                                                <mi>b</mi>
                                             </mrow>
                                          </msub>
                                       </mrow>
                                    </msub>
                                    <mi>u</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <msub>
                                       <mrow>
                                          <mi>L</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>b</mi>
                                       </mrow>
                                    </msub>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-close>]</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-bin>+</mo>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>[</mo>
                                    <msub>
                                       <mrow>
                                          <mi>c</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>T</mi>
                                       </mrow>
                                    </msub>
                                    <mi>u</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <mi>T</mi>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-close>]</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-bin>+</mo>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>[</mo>
                                    <msub>
                                       <mrow>
                                          <mi>c</mi>
                                       </mrow>
                                       <mrow>
                                          <msub>
                                             <mrow>
                                                <mi>L</mi>
                                             </mrow>
                                             <mrow>
                                                <mi>i</mi>
                                             </mrow>
                                          </msub>
                                       </mrow>
                                    </msub>
                                    <mi>u</mi>
                                    <mo class=MathClass-open>(</mo>
                                    <msub>
                                       <mrow>
                                          <mi>L</mi>
                                       </mrow>
                                       <mrow>
                                          <mn>1</mn>
                                       </mrow>
                                    </msub>
                                    <mo class=MathClass-close>)</mo>
                                    <mo class=MathClass-close>]</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </msqrt>
                     </math>
                  <td class=eq-no>(31)
            </table>
            <p>
               In the equation above,
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </math>
               is the sensitivity coefficient for each input parameter, and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </math>
               is standard uncertainty for each variable in the equation for
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </math>
               (the levels in the source and the receiving rooms, the reverberation time, the residual noise level, the surface area of the wall, the equivalent absorption area, the uncertainty of the measuring instruments).<br class=newline>In this approach, it is assumed that these input parameters are not correlated, which is mostly true for majority of parameters.<br class=newline>
            <p>
               When measurements are made using two loudspeaker positions, the calculation of the apparent sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </math>
               and the estimated measurement uncertainty
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-close>)</mo>
               </math>
               is made separately for each loudspeaker position, thus obtaining
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </math>
               and the corresponding uncertainty parameters.<br class=newline>
            <p>
               An example of the results for measurement uncertainties for the apparent sound reduction index
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </math>
               is shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-16.png data-img-alt="Figure 16. Experimental measurement uncertainty in repeatability conditions for different laboratories.">Figure 16</a> for each individual laboratory that participated in interlaboratory comparison. The results are compared with the tentative values for standard deviations in repeatability and reproducibility conditions given in ISO 12999-1:2020.
               <figure class=figure>
            <p><img src=${config.ASSETS_URL}/05/22/theory/figure-16.png alt="Figure 16. Experimental measurement uncertainty in repeatability conditions for different laboratories."><figcaption class=caption>Figure 16. Experimental measurement uncertainty in repeatability conditions for different laboratories.</figcaption></figure>
            <p>Repeatability conditions are obtained when an individual laboratory repeats the measurements several times and finds the standard deviations of measurement results. The reproducibility conditions are obtained when several laboratories repeat the measurements on the same building structure, using different equipment and measurement procedure.<br class=newline>
            <p><a href="#" data-img-src=${config.ASSETS_URL}/05/22/theory/figure-16.png data-img-alt="Figure 16. Experimental measurement uncertainty in repeatability conditions for different laboratories.">Figure 16</a> shows that different laboratories have different experimental uncertainty in each one-third octave band of interest. The obtained results are compared with the tentative values given in [7].
            <p>The averaged values for standard deviations in reproducibility conditions stated in ISO 12999-1:2020 can be used to estimate the measurement uncertainty of the single-number parameter. The tentative values of standard deviations in reproducibility conditions are added to the measurement curve to obtain the upper sound insulation curve and the upper single number value. After that, the tentative vales are subtracted from the measured sound insulation curve to obtain the lower sound insulation curve and the lower single-number value. These two values are subtracted and the difference is divided by 2, thus obtaining the experimental measurement uncertainty for the single-number value.<br class=newline>
            <p>
               Expanded measurement uncertainty can be found from combined experimental measurement uncertainty, the cover factor
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>k</mi>
               </math>
               , the interval of coverage (one-sided or two-sided) and the probability of covering the measurement result (84%, 95%, 99%).<br class=newline>
            <p>
               A common value of the combined measurement uncertainty is
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-rel>≈</mo>
               </math>
               1&nbsp;dB for the weighted apparent sound reduction index, and
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <mi>u</mi>
                  <mo class=MathClass-open>(</mo>
                  <msubsup>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>w</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-rel>≈</mo>
               </math>
               1.5&nbsp;dB for the weighted normalized impact sound pressure level if the measurement situation is as described in standards ISO 16283-1,2,3.
            <h3 class=sectionHead>References</h3>
            <dl class=thebibliography>
               <dt id=X0-ISO12354_3 class=thebibliography>[1]
               <dd id=bib-1 class=thebibliography>
                  <p><span class=cmti-10>Building acoustics — Estimation of acoustic performance of buildings from</span> <span class=cmti-10>the performance of elements — Part 3: Airborne sound insulation against</span> <span class=cmti-10>outdoor sound</span>. 2017.
               <dt id=X0-Hopkins2007 class=thebibliography>[2]
               <dd id=bib-2 class=thebibliography>
                  <p>Hopkins Carl. <span class=cmti-10>Sound Insulation</span>. 1. ed., [repr.] Routledge. New York: Taylor and Francis, 2007. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 978-0-7506-6526-1.
               <dt id=X0-ISO10140_2 class=thebibliography>[3]
               <dd id=bib-3 class=thebibliography>
                  <p><span class=cmti-10>ISO 10140-2:2010 Acoustics — Laboratory measurement of sound</span> <span class=cmti-10>insulation of building elements — Part 2: Measurement of airborne sound</span> <span class=cmti-10>insulation</span>. 2010.
               <dt id=X0-ISO10140_3 class=thebibliography>[4]
               <dd id=bib-4 class=thebibliography>
                  <p><span class=cmti-10>ISO 10140-3:2021 Acoustics — Laboratory measurement of sound</span> <span class=cmti-10>insulation of building elements — Part 3: Measurement of impact sound</span> <span class=cmti-10>insulation</span>. 2021.
               <dt id=X0-ISO12354_1 class=thebibliography>[5]
               <dd id=bib-5 class=thebibliography>
                  <p><span class=cmti-10>ISO 12354-1:2017: Building acoustics — Estimation of acoustic</span> <span class=cmti-10>performance of buildings from the performance of elements — Part 1:</span> <span class=cmti-10>Airborne sound insulation between rooms</span>. 2017.
               <dt id=X0-ISO12354_2 class=thebibliography>[6]
               <dd id=bib-6 class=thebibliography>
                  <p><span class=cmti-10>ISO 12354-2:2017 Building acoustics — Estimation of acoustic</span> <span class=cmti-10>performance of buildings from the performance of elements — Part 2:</span> <span class=cmti-10>Impact sound insulation between rooms</span>. 2017.
               <dt id=X0-ISO12999_1 class=thebibliography>[7]
               <dd id=bib-7 class=thebibliography>
                  <p><span class=cmti-10>ISO 12999-1:2020 Determination and handling of uncertainties in building</span> <span class=cmti-10>acoustics. Part 1: Sound insulation</span>. 2020.
               <dt id=X0-ISO16283_1 class=thebibliography>[8]
               <dd id=bib-8 class=thebibliography>
                  <p><span class=cmti-10>ISO 16283-1:2014/A1:2018: Acoustics – Field measurement of sound</span> <span class=cmti-10>insulation in buildings and of building elements – Part 1: Airborne sound</span> <span class=cmti-10>insulation</span>. 2018.
               <dt id=X0-ISO16283_3 class=thebibliography>[9]
               <dd id=bib-9 class=thebibliography>
                  <p><span class=cmti-10>ISO 16283-3:2016 Acoustics — Field measurement of sound insulation in</span> <span class=cmti-10>buildings and of building elements — Part 3: Fa</span><span class=cmti-10>ç</span><span class=cmti-10>ade sound insulation</span>. 2016.
               <dt id=X0-ISO717_1 class=thebibliography>[10]
               <dd id=bib-10 class=thebibliography>
                  <p><span class=cmti-10>ISO 717-1:2020 Acoustics. Rating of sound insulation in buildings and of</span> <span class=cmti-10>building elements – Part 1: Airborne sound insulation. </span>2020.
               <dt id=X0-ISO717_2 class=thebibliography>[11]
               <dd id=bib-11 class=thebibliography>
                  <p><span class=cmti-10>ISO 717-2:2020 Acoustics. Rating of sound insulation in buildings and of</span> <span class=cmti-10>building elements – Part 2: Impact sound insulation</span>. 2020.
               <dt id=X0-ISO17025 class=thebibliography>[12]
               <dd id=bib-12 class=thebibliography>
                  <p><span class=cmti-10>ISO/IEC 17025:2017 General requirements for the competence of testing</span> <span class=cmti-10>and calibration laboratories</span>. 2017.
               <dt id=X0-Machimbarrena class=thebibliography>[13]
               <dd id=bib-13 class=thebibliography>
                  <p>M. Machimbarrena. “ncertainty determination of in situ airborne sound insulation measurements”. In: <span class=cmti-10>Applied Acoustics </span>89 (2015).
               <dt id=X0-Monteiro class=thebibliography>[14]
               <dd id=bib-14 class=thebibliography>
                  <p>M Monteiro. “Individual uncertainty calculations of sound insulation measurements: a proposal of compromise values for 1/3 octave bands correlation coefficients”. In: <span class=cmti-10>EURONOISE 2018 </span>(2018).
               <dt id=X0-Rindel2018 class=thebibliography>[15]
               <dd id=bib-15 class=thebibliography>
                  <p>Jens H. Rindel. <span class=cmti-10>Sound Insulation in Buildings</span>. 1. ed., [repr.] CRC Press. 6000 Broken Sound Parkway NW,Suite 300 Boca Raton: Taylor and Francis, 2018. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9781498700429.
            </dl>
         </mspace>
</table>`;

export default theory;
